import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL from '../config';
import Button from '../components/Button';
import ThemePicker from '../components/ThemePicker';
import './SuperAdminDashboard.css';

interface Admin {
    _id: string;
    name: string;
    email: string;
    weddingCoupleName: string;
    weddingDate?: string;
    familySide: 'groom' | 'bride';
    createdAt: string;
}

interface Agency {
    _id: string;
    name: string;
    email: string;
    phone?: string;
    status: string;
    subscriptionPlan: string;
    createdAt: string;
}

interface CeremonySong {
    _id: string;
    weddingId: string;
    ceremonyName: string;
    songName: string;
    artist?: string;
    url: string;
}

interface Wedding {
    _id: string;
    coupleName: string;
    groomName: string;
    brideName: string;
    weddingDate: string;
    venue?: string;
    city?: string;
    bannerImage?: string;
    upiId?: string;
    upiName?: string;
    themeSongUrl?: string;
    themeSongName?: string;
    status: string;
    adminCount?: number;
    storageLimit?: number;
    storageUsage?: number;
    plan?: string;
    guestLimit?: number;
}

const PLAN_LIMITS = {
    basic: { storage: 10240, guests: 100 },
    silver: { storage: 102400, guests: 250 },
    gold: { storage: 1000000, guests: 1000 },
    custom: { storage: 512000, guests: 500 }
};

const SuperAdminDashboard: React.FC = () => {
    const history = useHistory();
    const { accessToken, user, loading: authLoading } = useAuth();

    const [admins, setAdmins] = useState<Admin[]>([]);
    const [agencies, setAgencies] = useState<Agency[]>([]);
    const [weddings, setWeddings] = useState<Wedding[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [activeTab, setActiveTab] = useState<'weddings' | 'admins' | 'agencies' | 'songs' | 'settings'>('weddings');
    const [editingWeddingId, setEditingWeddingId] = useState<string | null>(null);
    const [selectedWeddingForSongs, setSelectedWeddingForSongs] = useState<Wedding | null>(null);
    const [ceremonySongs, setCeremonySongs] = useState<CeremonySong[]>([]);
    const [agencySearchQuery, setAgencySearchQuery] = useState('');

    const [weddingFormData, setWeddingFormData] = useState({
        coupleName: '',
        groomName: '',
        brideName: '',
        weddingDate: '',
        venue: '',
        city: '',
        plan: 'basic',
        storageLimit: '10240',
        guestLimit: '100',
        upiId: '',
        upiName: '',
    });
    const [bannerFile, setBannerFile] = useState<File | null>(null);
    const [groomPhotoFile, setGroomPhotoFile] = useState<File | null>(null);
    const [bridePhotoFile, setBridePhotoFile] = useState<File | null>(null);
    const [themeSongFile, setThemeSongFile] = useState<File | null>(null);

    const [songFormData, setSongFormData] = useState({
        ceremonyName: 'Sangeet',
        songName: '',
        artist: '',
    });
    const [songFile, setSongFile] = useState<File | null>(null);

    const [adminFormData, setAdminFormData] = useState({
        name: '',
        email: '',
        password: '',
        phone: '',
        wedding: '',
        familySide: 'groom' as 'groom' | 'bride',
    });

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

    // Cultural Guest Terms & Conditions States
    const [termsData, setTermsData] = useState<Record<string, string[]>>({
        en: [], hi: [], fr: [], es: [], de: []
    });
    const [selectedTermsLang, setSelectedTermsLang] = useState<'en' | 'hi' | 'fr' | 'es' | 'de'>('en');
    const [termsLoading, setTermsLoading] = useState(false);
    const [termsSaving, setTermsSaving] = useState(false);
    const [termsMessage, setTermsMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        if (!authLoading && (!user || user.role !== 'superadmin')) {
            history.push('/superadmin/login');
        }
    }, [user, authLoading, history]);

    // Fetch all weddings
    const fetchWeddings = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/superadmin/weddings`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            const data = await response.json();
            if (response.ok) {
                setWeddings(data.weddings);
            } else {
                setError(data.message || 'Failed to fetch weddings');
            }
        } catch (err: any) {
            console.error('Error fetching weddings:', err);
            setError('Failed to connect to server');
        }
    };

    // Fetch all admins
    const fetchAdmins = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/superadmin/admins`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            const data = await response.json();
            if (response.ok) {
                setAdmins(data.admins);
            } else {
                setError(data.message || 'Failed to fetch admins');
            }
        } catch (err: any) {
            console.error('Error fetching admins:', err);
            setError('Failed to connect to server');
        }
    };

    const fetchAgencies = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/superadmin/agencies`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            const data = await response.json();
            if (response.ok) {
                setAgencies(data.agencies || []);
            }
        } catch (err: any) {
            console.error('Error fetching agencies:', err);
        }
    };

    const fetchTerms = async () => {
        setTermsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/superadmin/cultural-guest/terms`, {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            const data = await response.json();
            if (response.ok && data.success) {
                setTermsData(data.terms);
            }
        } catch (err) {
            console.error('Error fetching terms:', err);
        } finally {
            setTermsLoading(false);
        }
    };

    const saveTerms = async () => {
        setTermsSaving(true);
        setTermsMessage({ type: '', text: '' });
        try {
            const response = await fetch(`${API_BASE_URL}/api/superadmin/cultural-guest/terms`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify({ terms: termsData })
            });
            const data = await response.json();
            if (response.ok && data.success) {
                setTermsMessage({ type: 'success', text: 'Terms & Conditions saved successfully!' });
                setTermsData(data.terms);
            } else {
                setTermsMessage({ type: 'error', text: data.message || 'Failed to save terms' });
            }
        } catch (err) {
            setTermsMessage({ type: 'error', text: 'Server error occurred' });
        } finally {
            setTermsSaving(false);
        }
    };

    useEffect(() => {
        if (accessToken) {
            fetchWeddings();
            fetchAdmins();
            fetchAgencies();
            fetchTerms();
        }
    }, [accessToken]);

    const handleWeddingInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setWeddingFormData({
            ...weddingFormData,
            [e.target.name]: e.target.value,
        });
    };

    const handlePlanChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedPlan = e.target.value as keyof typeof PLAN_LIMITS;
        const limits = PLAN_LIMITS[selectedPlan];

        setWeddingFormData({
            ...weddingFormData,
            plan: selectedPlan,
            storageLimit: limits.storage.toString(),
            guestLimit: limits.guests.toString(),
        });
    };

    const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setBannerFile(e.target.files[0]);
        }
    };

    const handleGroomPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setGroomPhotoFile(e.target.files[0]);
        }
    };

    const handleBridePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setBridePhotoFile(e.target.files[0]);
        }
    };

    const handleThemeSongChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setThemeSongFile(e.target.files[0]);
        }
    };

    const handleSongFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSongFile(e.target.files[0]);
        }
    };

    const handleManageSongs = async (wedding: Wedding) => {
        setSelectedWeddingForSongs(wedding);
        setActiveTab('songs');
        fetchCeremonySongs(wedding._id);
    };

    const fetchCeremonySongs = async (weddingId: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/superadmin/wedding/${weddingId}/ceremony-songs`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const data = await response.json();
            if (response.ok) {
                setCeremonySongs(data.songs);
            }
        } catch (err) {
            console.error('Error fetching songs:', err);
        }
    };

    const handleAddSong = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedWeddingForSongs || !songFile) return;

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const formData = new FormData();
            formData.append('ceremonyName', songFormData.ceremonyName);
            formData.append('songName', songFormData.songName);
            formData.append('artist', songFormData.artist);
            formData.append('song', songFile);

            const response = await fetch(`${API_BASE_URL}/api/superadmin/wedding/${selectedWeddingForSongs._id}/ceremony-song`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${accessToken}` },
                body: formData,
            });

            const data = await response.json();
            if (response.ok) {
                setSuccess('Song added successfully!');
                setSongFormData({ ceremonyName: 'Sangeet', songName: '', artist: '' });
                setSongFile(null);
                fetchCeremonySongs(selectedWeddingForSongs._id);
            } else {
                setError(data.message || 'Failed to add song');
            }
        } catch (err) {
            setError('Failed to connect to server');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSong = async (songId: string) => {
        if (!window.confirm('Delete this song?')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/api/superadmin/ceremony-song/${songId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            if (response.ok) {
                setSuccess('Song deleted');
                if (selectedWeddingForSongs) fetchCeremonySongs(selectedWeddingForSongs._id);
            }
        } catch (err) {
            setError('Failed to delete');
        }
    };

    const handleEditWedding = (wedding: Wedding) => {
        setEditingWeddingId(wedding._id);
        setWeddingFormData({
            coupleName: wedding.coupleName,
            groomName: wedding.groomName,
            brideName: wedding.brideName,
            weddingDate: wedding.weddingDate.split('T')[0], // Extract date part for input
            venue: wedding.venue || '',
            city: wedding.city || '',
            plan: wedding.plan || 'basic',
            storageLimit: wedding.storageLimit?.toString() || '10240',
            guestLimit: wedding.guestLimit?.toString() || '100',
            upiId: wedding.upiId || '',
            upiName: wedding.upiName || '',
        });
        setActiveTab('weddings');
        // Scroll to top to see the form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const cancelWeddingEdit = () => {
        setEditingWeddingId(null);
        setWeddingFormData({
            coupleName: '',
            groomName: '',
            brideName: '',
            weddingDate: '',
            venue: '',
            city: '',
            plan: 'basic',
            storageLimit: '10240',
            guestLimit: '100',
            upiId: '',
            upiName: '',
        });
        setBannerFile(null);
        setGroomPhotoFile(null);
        setBridePhotoFile(null);
        setThemeSongFile(null);
    };

    const handleCreateWedding = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const formData = new FormData();
            formData.append('coupleName', weddingFormData.coupleName);
            formData.append('groomName', weddingFormData.groomName);
            formData.append('brideName', weddingFormData.brideName);
            formData.append('weddingDate', weddingFormData.weddingDate);
            formData.append('venue', weddingFormData.venue);
            formData.append('city', weddingFormData.city);
            formData.append('storageLimit', weddingFormData.storageLimit);
            formData.append('plan', weddingFormData.plan);
            formData.append('guestLimit', weddingFormData.guestLimit);
            formData.append('upiId', weddingFormData.upiId);
            formData.append('upiName', weddingFormData.upiName);
            if (bannerFile) {
                formData.append('banner', bannerFile);
            }
            if (groomPhotoFile) {
                formData.append('groomPhoto', groomPhotoFile);
            }
            if (bridePhotoFile) {
                formData.append('bridePhoto', bridePhotoFile);
            }
            if (themeSongFile) {
                formData.append('themeSong', themeSongFile);
            }

            const url = editingWeddingId
                ? `${API_BASE_URL}/api/superadmin/wedding/${editingWeddingId}`
                : `${API_BASE_URL}/api/superadmin/wedding`;

            const method = editingWeddingId ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: formData,
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `Failed to ${editingWeddingId ? 'update' : 'create'} wedding`);
            }

            setSuccess(`Wedding ${editingWeddingId ? 'updated' : 'created'} successfully!`);
            cancelWeddingEdit();
            fetchWeddings();
        } catch (err: any) {
            setError(err.message || `Failed to ${editingWeddingId ? 'update' : 'create'} wedding`);
        } finally {
            setLoading(false);
        }
    };

    const handleAdminInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setAdminFormData({
            ...adminFormData,
            [e.target.name]: e.target.value,
        });
    };

    const handleCreateAdmin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const selectedWedding = weddings.find(w => w._id === adminFormData.wedding);
            console.log('Creating admin with payload:', {
                ...adminFormData,
                weddingCoupleName: selectedWedding?.coupleName,
                weddingDate: selectedWedding?.weddingDate,
            });

            const response = await fetch(`${API_BASE_URL}/api/superadmin/create-admin`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    ...adminFormData,
                    weddingCoupleName: selectedWedding?.coupleName,
                    weddingDate: selectedWedding?.weddingDate,
                }),
            });

            const data = await response.json();
            console.log('Create admin response:', data);

            if (!response.ok) {
                throw new Error(data.message || 'Failed to create admin');
            }

            setSuccess('Admin created successfully!');
            setAdminFormData({
                name: '',
                email: '',
                password: '',
                phone: '',
                wedding: '',
                familySide: 'groom' as 'groom' | 'bride',
            });

            fetchAdmins();
            fetchWeddings();
        } catch (err: any) {
            setError(err.message || 'Failed to create admin');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAdmin = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this admin?')) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/superadmin/admin/${id}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (response.ok) {
                setSuccess('Admin deleted successfully');
                fetchAdmins();
                fetchWeddings();
            }
        } catch (err) {
            setError('Failed to delete admin');
        }
    };

    const handleDeleteWedding = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this wedding?')) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/superadmin/wedding/${id}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message);
            }

            setSuccess('Wedding deleted successfully');
            fetchWeddings();
        } catch (err: any) {
            setError(err.message || 'Failed to delete wedding');
        }
    };

    const handleUpdateAgencyStatus = async (id: string, status: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/superadmin/agencies/${id}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ status })
            });
            const data = await response.json();
            if (response.ok) {
                setSuccess(`Agency marked as ${status}`);
                fetchAgencies();
            } else {
                setError(data.message || 'Failed to update agency status');
            }
        } catch (err: any) {
            setError('Error updating agency status');
        }
    };

    if (authLoading) return <div className="loading">Loading...</div>;

    return (
        <div className="dashboard-container">
            <div className="dashboard-content">
                <div className="dashboard-header">
                    <h1 className="dashboard-title">SuperAdmin Dashboard</h1>
                    <p className="dashboard-subtitle">Welcome back, {user?.name}! Manage weddings and admins.</p>
                </div>

                <div className="dashboard-grid">
                    <div className="stat-card">
                        <div className="stat-card-header">
                            <span className="stat-card-title">Total Weddings</span>
                            <div className="stat-card-icon purple">💍</div>
                        </div>
                        <div className="stat-card-value">{weddings.length}</div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-card-header">
                            <span className="stat-card-title">Total Admins</span>
                            <div className="stat-card-icon purple">👥</div>
                        </div>
                        <div className="stat-card-value">{admins.length}</div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-card-header">
                            <span className="stat-card-title">Pending Agencies</span>
                            <div className="stat-card-icon purple">🏢</div>
                        </div>
                        <div className="stat-card-value">{agencies.filter(a => a.status === 'pending').length}</div>
                    </div>

                    <div className="stat-card" style={{ cursor: 'pointer', background: 'linear-gradient(135deg, #fdfcfb 0%, #e2d1c3 100%)' }} onClick={() => history.push('/superadmin/marketplace')}>
                        <div className="stat-card-header">
                            <span className="stat-card-title">Marketplace</span>
                            <div className="stat-card-icon purple" style={{ filter: 'hue-rotate(90deg)' }}>🏪</div>
                        </div>
                        <div className="stat-card-value" style={{ fontSize: '1.25rem', color: '#1e1e2e', marginTop: '0.75rem', fontFamily: 'Inter, sans-serif' }}>Manage Vendors &rarr;</div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-card-header">
                            <span className="stat-card-title">Active Weddings</span>
                            <div className="stat-card-icon purple">🎊</div>
                        </div>
                        <div className="stat-card-value">
                            {weddings.filter((w) => w.status === 'upcoming' || w.status === 'ongoing').length}
                        </div>
                    </div>
                </div>

                <div className="tabs">
                    <button
                        className={`tab ${activeTab === 'weddings' ? 'active' : ''}`}
                        onClick={() => setActiveTab('weddings')}
                    >
                        💍 Weddings
                    </button>
                    <button
                        className={`tab ${activeTab === 'admins' ? 'active' : ''}`}
                        onClick={() => setActiveTab('admins')}
                    >
                        👥 Admins
                    </button>
                    <button
                        className={`tab ${activeTab === 'agencies' ? 'active' : ''}`}
                        onClick={() => setActiveTab('agencies')}
                    >
                        🏢 Agencies
                    </button>
                    <button
                        className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
                        onClick={() => setActiveTab('settings')}
                    >
                        ⚙️ Settings
                    </button>
                    {selectedWeddingForSongs && (
                        <button
                            className={`tab ${activeTab === 'songs' ? 'active' : ''}`}
                            onClick={() => setActiveTab('songs')}
                        >
                            🎵 Songs: {selectedWeddingForSongs.coupleName}
                        </button>
                    )}
                </div>

                {error && <div className="error-message">{error}</div>}
                {success && <div className="success-message">{success}</div>}

                {activeTab === 'settings' ? (
                    <>
                    <div className="section-card">
                        <ThemePicker />
                    </div>
                    <div className="section-card">
                        <h2 className="section-title">🔒 Change Password</h2>
                        <div className="create-admin-form" style={{ maxWidth: '500px' }}>
                            {passwordMessage.text && (
                                <div style={{
                                    padding: '10px',
                                    marginBottom: '1rem',
                                    borderRadius: '8px',
                                    background: passwordMessage.type === 'error' ? 'rgba(255, 0, 0, 0.1)' : 'rgba(0, 255, 0, 0.1)',
                                    color: passwordMessage.type === 'error' ? '#ff6b6b' : '#51cf66',
                                    border: `1px solid ${passwordMessage.type === 'error' ? '#ff6b6b' : '#51cf66'}`,
                                    gridColumn: '1 / -1'
                                }}>
                                    {passwordMessage.text}
                                </div>
                            )}

                            <div className="form-group-inline" style={{ gridColumn: '1 / -1' }}>
                                <label>Current Password</label>
                                <input
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    placeholder="Enter current password"
                                />
                            </div>

                            <div className="form-group-inline" style={{ gridColumn: '1 / -1' }}>
                                <label>New Password</label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Enter new password"
                                />
                            </div>

                            <div className="form-group-inline" style={{ gridColumn: '1 / -1' }}>
                                <label>Confirm New Password</label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm new password"
                                />
                            </div>

                            <div className="form-actions" style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
                                <Button
                                    label={passwordSaving ? 'Updating...' : 'Change Password'}
                                    onClick={async () => {
                                        setPasswordMessage({ type: '', text: '' });
                                        if (!currentPassword || !newPassword || !confirmPassword) {
                                            setPasswordMessage({ type: 'error', text: 'All fields are required' });
                                            return;
                                        }
                                        if (newPassword !== confirmPassword) {
                                            setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
                                            return;
                                        }
                                        if (newPassword.length < 6) {
                                            setPasswordMessage({ type: 'error', text: 'Password must be at least 6 characters' });
                                            return;
                                        }

                                        setPasswordSaving(true);
                                        try {
                                            const response = await fetch(`${API_BASE_URL}/api/superadmin/change-password`, {
                                                method: 'PUT',
                                                headers: {
                                                    'Content-Type': 'application/json',
                                                    Authorization: `Bearer ${accessToken}`,
                                                },
                                                body: JSON.stringify({ currentPassword, newPassword }),
                                            });
                                            const data = await response.json();
                                            if (response.ok) {
                                                setPasswordMessage({ type: 'success', text: 'Password successfully changed!' });
                                                setCurrentPassword('');
                                                setNewPassword('');
                                                setConfirmPassword('');
                                            } else {
                                                setPasswordMessage({ type: 'error', text: data.message || 'Failed to change password' });
                                            }
                                        } catch (err) {
                                            setPasswordMessage({ type: 'error', text: 'Server error occurred' });
                                        } finally {
                                            setPasswordSaving(false);
                                        }
                                    }}
                                    disabled={passwordSaving}
                                />
                            </div>
                        </div>
                    </div>
                    
                    {/* Cultural Guest Program Terms & Conditions Editor */}
                    <div className="section-card" style={{ marginTop: '2rem' }}>
                        <h2 className="section-title">📜 Cultural Guest - Terms & Conditions</h2>
                        <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '1.5rem' }}>
                            Edit the guidelines and terms and conditions shown to foreign travelers during registration. Each line represents a rule.
                        </p>

                        <div style={{ display: 'flex', gap: '8px', marginBottom: '1.5rem', borderBottom: '1px solid rgba(197, 160, 89, 0.15)', paddingBottom: '0.75rem', flexWrap: 'wrap' }}>
                            {(['en', 'hi', 'fr', 'es', 'de'] as const).map(langCode => (
                                <button
                                    key={langCode}
                                    type="button"
                                    onClick={() => setSelectedTermsLang(langCode)}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        background: selectedTermsLang === langCode ? '#C5A059' : 'transparent',
                                        color: selectedTermsLang === langCode ? '#1e1e2e' : '#ffffff',
                                        border: '1px solid rgba(197, 160, 89, 0.4)',
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        textTransform: 'uppercase',
                                        fontSize: '0.8rem',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {langCode === 'en' && '🇬🇧 English'}
                                    {langCode === 'hi' && '🇮🇳 हिन्दी'}
                                    {langCode === 'fr' && '🇫🇷 Français'}
                                    {langCode === 'es' && '🇪🇸 Español'}
                                    {langCode === 'de' && '🇩🇪 Deutsch'}
                                </button>
                            ))}
                        </div>

                        {termsMessage.text && (
                            <div style={{
                                padding: '10px 15px',
                                marginBottom: '1rem',
                                borderRadius: '8px',
                                background: termsMessage.type === 'error' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                                color: termsMessage.type === 'error' ? '#ef4444' : '#10b981',
                                border: `1px solid ${termsMessage.type === 'error' ? '#ef4444' : '#10b981'}`,
                                fontSize: '0.9rem'
                            }}>
                                {termsMessage.text}
                            </div>
                        )}

                        {termsLoading ? (
                            <p>Loading terms data...</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
                                {(termsData[selectedTermsLang] || []).map((term, index) => (
                                    <div key={index} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <span style={{ fontWeight: 600, color: '#C5A059', minWidth: '20px' }}>{index + 1}.</span>
                                        <input
                                            type="text"
                                            value={term}
                                            onChange={(e) => {
                                                const updated = [...(termsData[selectedTermsLang] || [])];
                                                updated[index] = e.target.value;
                                                setTermsData({ ...termsData, [selectedTermsLang]: updated });
                                            }}
                                            placeholder="Enter rule / term..."
                                            style={{
                                                flex: 1,
                                                padding: '0.75rem 1rem',
                                                borderRadius: '10px',
                                                border: '1px solid rgba(197, 160, 89, 0.3)',
                                                background: 'rgba(255, 255, 255, 0.03)',
                                                color: '#ffffff',
                                                fontFamily: 'Inter, sans-serif',
                                                outline: 'none'
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const updated = (termsData[selectedTermsLang] || []).filter((_, i) => i !== index);
                                                setTermsData({ ...termsData, [selectedTermsLang]: updated });
                                            }}
                                            style={{
                                                background: 'rgba(239, 68, 68, 0.15)',
                                                border: '1px solid #ef4444',
                                                color: '#ef4444',
                                                borderRadius: '8px',
                                                padding: '0.6rem 0.8rem',
                                                cursor: 'pointer',
                                                fontWeight: 600
                                            }}
                                            title="Remove Term"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                ))}

                                <div style={{ display: 'flex', gap: '10px', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const updated = [...(termsData[selectedTermsLang] || []), ''];
                                            setTermsData({ ...termsData, [selectedTermsLang]: updated });
                                        }}
                                        style={{
                                            background: 'rgba(197, 160, 89, 0.1)',
                                            border: '1px solid #C5A059',
                                            color: '#C5A059',
                                            borderRadius: '8px',
                                            padding: '0.6rem 1.2rem',
                                            cursor: 'pointer',
                                            fontWeight: 600,
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        ➕ Add Term Line
                                    </button>
                                    <button
                                        type="button"
                                        onClick={saveTerms}
                                        disabled={termsSaving}
                                        style={{
                                            background: 'linear-gradient(135deg, #C5A059 0%, #A8844A 50%, #C5A059 100%)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '0.6rem 2rem',
                                            cursor: 'pointer',
                                            fontWeight: 700,
                                            fontSize: '0.85rem',
                                            boxShadow: '0 4px 15px rgba(197, 160, 89, 0.2)'
                                        }}
                                    >
                                        {termsSaving ? 'Saving...' : '💾 Save All Terms'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    </>
                ) : activeTab === 'agencies' ? (
                    <div className="section-card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                            <h2 className="section-title" style={{ margin: 0 }}>🏢 Agencies Management</h2>
                            <input 
                                type="text" 
                                placeholder="Search by name, email, or phone..." 
                                value={agencySearchQuery}
                                onChange={(e) => setAgencySearchQuery(e.target.value)}
                                style={{
                                    padding: '0.75rem 1rem',
                                    borderRadius: '12px',
                                    border: '1px solid var(--gold-border)',
                                    minWidth: '280px',
                                    fontFamily: 'Inter, sans-serif',
                                    outline: 'none'
                                }}
                                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.boxShadow = '0 0 0 4px var(--gold-shadow)'; }}
                                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--gold-border)'; e.currentTarget.style.boxShadow = 'none'; }}
                            />
                        </div>
                        {agencies.length === 0 ? (
                            <p className="empty-state">No agencies registered yet.</p>
                        ) : (
                            <div className="wedding-card-grid">
                                {agencies
                                    .filter(agency => 
                                        agency.name.toLowerCase().includes(agencySearchQuery.toLowerCase()) || 
                                        agency.email.toLowerCase().includes(agencySearchQuery.toLowerCase()) ||
                                        (agency.phone && agency.phone.includes(agencySearchQuery))
                                    )
                                    .map((agency) => (
                                    <div key={agency._id} className="wedding-admin-card">
                                        <div className="wedding-card-top" style={{ alignItems: 'flex-start' }}>
                                            <div className="wedding-card-info" style={{ flex: 1, paddingRight: '1rem' }}>
                                                <h3>{agency.name}</h3>
                                                <div className="wedding-card-meta" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem', marginTop: '0.75rem' }}>
                                                    <span className="meta-tag" style={{ wordBreak: 'break-all' }}>✉️ {agency.email}</span>
                                                    <span className="meta-tag">📞 {agency.phone || 'N/A'}</span>
                                                    <span className="meta-tag" style={{ textTransform: 'capitalize' }}>📄 Plan: {agency.subscriptionPlan}</span>
                                                </div>
                                            </div>
                                            <div className="wedding-card-theme-status" style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                                                <span className={`status-badge ${agency.status}`}>
                                                    {agency.status}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="wedding-card-actions" style={{ marginTop: 'auto', paddingTop: '1.25rem', borderTop: '1px solid rgba(197, 160, 89, 0.15)' }}>
                                            <div className="action-row-end">
                                                {agency.status === 'pending' && (
                                                    <>
                                                        <button 
                                                            style={{ flex: 1, backgroundColor: '#2ecc71', color: 'white', padding: '10px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }} 
                                                            onClick={() => handleUpdateAgencyStatus(agency._id, 'active')}
                                                        >
                                                            Approve
                                                        </button>
                                                        <button 
                                                            style={{ flex: 1, backgroundColor: '#e74c3c', color: 'white', padding: '10px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }} 
                                                            onClick={() => handleUpdateAgencyStatus(agency._id, 'rejected')}
                                                        >
                                                            Reject
                                                        </button>
                                                    </>
                                                )}
                                                {agency.status === 'active' && (
                                                    <button 
                                                        className="cancel-btn-premium" 
                                                        style={{ flex: 1, margin: 0 }} 
                                                        onClick={() => handleUpdateAgencyStatus(agency._id, 'suspended')}
                                                    >
                                                        Suspend
                                                    </button>
                                                )}
                                                {agency.status === 'suspended' && (
                                                    <button 
                                                        style={{ flex: 1, backgroundColor: '#2ecc71', color: 'white', padding: '10px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }} 
                                                        onClick={() => handleUpdateAgencyStatus(agency._id, 'active')}
                                                    >
                                                        Activate
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : activeTab === 'weddings' ? (
                    <>
                        <div className="section-card">
                            <h2 className="section-title">
                                {editingWeddingId ? '📝 Edit Wedding' : '➕ Create New Wedding'}
                            </h2>
                            <form onSubmit={handleCreateWedding}>
                                <div className="create-admin-form">
                                    <div className="form-group-inline">
                                        <label htmlFor="coupleName">Couple Name *</label>
                                        <input type="text" id="coupleName" name="coupleName" value={weddingFormData.coupleName} onChange={handleWeddingInputChange} placeholder="John & Jane" required />
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="groomName">Groom's Name *</label>
                                        <input type="text" id="groomName" name="groomName" value={weddingFormData.groomName} onChange={handleWeddingInputChange} placeholder="John Doe" required />
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="brideName">Bride's Name *</label>
                                        <input type="text" id="brideName" name="brideName" value={weddingFormData.brideName} onChange={handleWeddingInputChange} placeholder="Jane Smith" required />
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="weddingDate">Wedding Date *</label>
                                        <input type="date" id="weddingDate" name="weddingDate" value={weddingFormData.weddingDate} onChange={handleWeddingInputChange} min={new Date().toISOString().split('T')[0]} required />
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="venue">Venue</label>
                                        <input type="text" id="venue" name="venue" value={weddingFormData.venue} onChange={handleWeddingInputChange} placeholder="Grand Hotel" />
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="city">City</label>
                                        <input type="text" id="city" name="city" value={weddingFormData.city} onChange={handleWeddingInputChange} placeholder="New York" />
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="plan">Select Plan *</label>
                                        <select id="plan" name="plan" value={weddingFormData.plan} onChange={handlePlanChange} required>
                                            <option value="basic">Basic (10GB, 100 Guests)</option>
                                            <option value="silver">Silver (100GB, 250 Guests)</option>
                                            <option value="gold">Gold (Unlimited, 100k Guests)</option>
                                            <option value="custom">Custom</option>
                                        </select>
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="storageLimit">Storage Limit (MB) *</label>
                                        <input type="number" id="storageLimit" name="storageLimit" value={weddingFormData.storageLimit} onChange={handleWeddingInputChange} min="10" required />
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="guestLimit">Guest Invitation Limit *</label>
                                        <input type="number" id="guestLimit" name="guestLimit" value={weddingFormData.guestLimit} onChange={handleWeddingInputChange} min="1" required />
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="upiId">UPI ID (for Shagun)</label>
                                        <input type="text" id="upiId" name="upiId" value={weddingFormData.upiId} onChange={handleWeddingInputChange} placeholder="9876543210@upi" />
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="upiName">UPI Holder Name</label>
                                        <input type="text" id="upiName" name="upiName" value={weddingFormData.upiName} onChange={handleWeddingInputChange} placeholder="Bride/Groom Name" />
                                    </div>
                                    <div className="form-group-inline form-group-full">
                                        <label htmlFor="banner">Advertisement Banner</label>
                                        <input type="file" id="banner" name="banner" accept="image/*" onChange={handleBannerChange} />
                                        <small>Max 5MB</small>
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="groomPhoto">Groom Photo</label>
                                        <input type="file" id="groomPhoto" name="groomPhoto" accept="image/*" onChange={handleGroomPhotoChange} />
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="bridePhoto">Bride Photo</label>
                                        <input type="file" id="bridePhoto" name="bridePhoto" accept="image/*" onChange={handleBridePhotoChange} />
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="themeSong">Wedding Theme Song</label>
                                        <input type="file" id="themeSong" name="themeSong" accept="audio/*" onChange={handleThemeSongChange} />
                                        <small>MP3, WAV, M4A (Max 15MB)</small>
                                    </div>
                                </div>
                                <div className="form-actions">
                                    <Button
                                        label={loading ? (editingWeddingId ? 'Updating...' : 'Creating...') : (editingWeddingId ? 'Update Wedding' : 'Create Wedding')}
                                        onClick={() => { }}
                                        disabled={loading}
                                    />
                                    {editingWeddingId && (
                                        <button
                                            type="button"
                                            className="cancel-btn-premium"
                                            onClick={cancelWeddingEdit}
                                        >
                                            Cancel Edit
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>

                        <div className="section-card">
                            <h2 className="section-title">💍 All Weddings</h2>
                            {weddings.length === 0 ? (
                                <p className="empty-state">No weddings found.</p>
                            ) : (
                                <div className="wedding-card-grid">
                                    {weddings.map((wedding) => (
                                        <div key={wedding._id} className="wedding-admin-card">
                                            <div className="wedding-card-top">
                                                <div className="wedding-card-info">
                                                    <h3>{wedding.coupleName}</h3>
                                                    <div className="wedding-card-meta">
                                                        <span className="meta-tag date">📅 {new Date(wedding.weddingDate).toLocaleDateString()}</span>
                                                        <span className={`plan-badge ${wedding.plan || 'basic'}`}>{wedding.plan || 'basic'}</span>
                                                    </div>
                                                </div>
                                                <div className="wedding-card-theme-status">
                                                    {wedding.themeSongUrl ?
                                                        <span className="theme-pill enabled" title="Music Enabled">🎵</span> :
                                                        <span className="theme-pill disabled" title="No Music">🔇</span>
                                                    }
                                                </div>
                                            </div>

                                            <div className="wedding-card-stats">
                                                <div className="stat-row">
                                                    <span className="stat-label">Admins</span>
                                                    <span className="stat-value">{wedding.adminCount}/2</span>
                                                </div>
                                                <div className="stat-row">
                                                    <span className="stat-label">Guest Limit</span>
                                                    <span className="stat-value">{wedding.guestLimit || 100}</span>
                                                </div>
                                                <div className="stat-progress-block">
                                                    <div className="stat-progress-header">
                                                        <span className="stat-label">Storage Usage</span>
                                                        <span className="stat-value">
                                                            {(wedding.storageUsage ? (wedding.storageUsage / (1024 * 1024)).toFixed(1) : 0)}MB / {wedding.storageLimit}MB
                                                        </span>
                                                    </div>
                                                    <div className="progress-track">
                                                        <div className="progress-fill" style={{
                                                            width: `${Math.min(100, (wedding.storageUsage || 0) / ((wedding.storageLimit || 1) * 1024 * 1024) * 100)}%`,
                                                            background: (wedding.storageUsage || 0) / ((wedding.storageLimit || 1) * 1024 * 1024) > 0.9 ? '#ff5252' : 'var(--gold)'
                                                        }}></div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
                                                <button
                                                    onClick={() => handleManageSongs(wedding)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '12px',
                                                        background: 'linear-gradient(135deg, #C5A059 0%, #A8844A 50%, #C5A059 100%)',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '12px',
                                                        fontWeight: 700,
                                                        fontSize: '0.9rem',
                                                        cursor: 'pointer',
                                                        fontFamily: 'Inter, sans-serif',
                                                    }}
                                                >
                                                    🎵 Manage Music
                                                </button>
                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                    <button
                                                        onClick={() => handleEditWedding(wedding)}
                                                        style={{
                                                            flex: 1,
                                                            padding: '10px',
                                                            background: 'transparent',
                                                            color: '#C5A059',
                                                            border: '1.5px solid rgba(197, 160, 89, 0.4)',
                                                            borderRadius: '10px',
                                                            fontWeight: 600,
                                                            fontSize: '0.85rem',
                                                            cursor: 'pointer',
                                                            fontFamily: 'Inter, sans-serif',
                                                        }}
                                                    >
                                                        ✏️ Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteWedding(wedding._id)}
                                                        style={{
                                                            flex: 1,
                                                            padding: '10px',
                                                            background: 'rgba(229, 115, 115, 0.08)',
                                                            color: '#E57373',
                                                            border: '1.5px solid rgba(229, 115, 115, 0.25)',
                                                            borderRadius: '10px',
                                                            fontWeight: 600,
                                                            fontSize: '0.85rem',
                                                            cursor: 'pointer',
                                                            fontFamily: 'Inter, sans-serif',
                                                        }}
                                                    >
                                                        🗑️ Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                ) : activeTab === 'admins' ? (
                    <>
                        <div className="section-card">
                            <h2 className="section-title">➕ Create New Admin</h2>
                            <form onSubmit={handleCreateAdmin}>
                                <div className="create-admin-form">
                                    <div className="form-group-inline">
                                        <label htmlFor="wedding">Select Wedding *</label>
                                        <select id="wedding" name="wedding" value={adminFormData.wedding} onChange={handleAdminInputChange} required>
                                            <option value="">Choose...</option>
                                            {weddings.map(w => <option key={w._id} value={w._id}>{w.coupleName}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="familySide">Family Side *</label>
                                        <select id="familySide" name="familySide" value={adminFormData.familySide} onChange={handleAdminInputChange} required>
                                            <option value="groom">Groom</option>
                                            <option value="bride">Bride</option>
                                        </select>
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="name">Name *</label>
                                        <input type="text" id="name" name="name" value={adminFormData.name} onChange={handleAdminInputChange} required />
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="email">Email *</label>
                                        <input type="email" id="email" name="email" value={adminFormData.email} onChange={handleAdminInputChange} required />
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="password">Password *</label>
                                        <input type="password" id="password" name="password" value={adminFormData.password} onChange={handleAdminInputChange} minLength={6} required />
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="phone">Phone</label>
                                        <input type="text" id="phone" name="phone" value={adminFormData.phone} onChange={handleAdminInputChange} />
                                    </div>
                                </div>
                                <div className="form-actions">
                                    <Button label={loading ? 'Creating...' : 'Create Admin'} onClick={() => { }} disabled={loading || weddings.length === 0} />
                                </div>
                            </form>
                        </div>

                        <div className="section-card">
                            <h2 className="section-title">👥 All Admins</h2>
                            {admins.length === 0 ? (
                                <p>No admins found.</p>
                            ) : (
                                <div className="wedding-card-grid">
                                    {admins.map((admin) => (
                                        <div key={admin._id} className="wedding-admin-card">
                                            <div className="wedding-card-top">
                                                <div className="wedding-card-info">
                                                    <h3>{admin.name}</h3>
                                                    <div className="wedding-card-meta">
                                                        <span className="meta-tag">✉️ {admin.email}</span>
                                                    </div>
                                                </div>
                                                <span className={`plan-badge ${admin.familySide}`} style={{
                                                    background: admin.familySide === 'groom' ? '#dbeafe' : '#fce7f3',
                                                    color: admin.familySide === 'groom' ? '#1e40af' : '#be185d',
                                                    border: 'none',
                                                    padding: '4px 12px',
                                                    fontSize: '0.75rem',
                                                    textTransform: 'capitalize',
                                                }}>
                                                    {admin.familySide === 'groom' ? '🤵' : '👰'} {admin.familySide}
                                                </span>
                                            </div>

                                            <div className="wedding-card-stats">
                                                <div className="stat-row">
                                                    <span className="stat-label">Assigned Wedding</span>
                                                    <span className="stat-value">{admin.weddingCoupleName}</span>
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => handleDeleteAdmin(admin._id)}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    background: 'rgba(229, 115, 115, 0.08)',
                                                    color: '#E57373',
                                                    border: '1.5px solid rgba(229, 115, 115, 0.25)',
                                                    borderRadius: '10px',
                                                    fontWeight: 600,
                                                    fontSize: '0.85rem',
                                                    cursor: 'pointer',
                                                    fontFamily: 'Inter, sans-serif',
                                                    marginTop: '4px',
                                                }}
                                            >
                                                🗑️ Delete Admin
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <div className="section-card">
                            <h2 className="section-title">🎵 Add Ceremony Song for {selectedWeddingForSongs?.coupleName}</h2>
                            <form onSubmit={handleAddSong}>
                                <div className="create-admin-form">
                                    <div className="form-group-inline">
                                        <label htmlFor="ceremonyName">Ceremony Name *</label>
                                        <input
                                            type="text"
                                            id="ceremonyName"
                                            value={songFormData.ceremonyName}
                                            onChange={(e) => setSongFormData({ ...songFormData, ceremonyName: e.target.value })}
                                            placeholder="Sangeet, Mehendi, etc."
                                            required
                                        />
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="songName">Song Title *</label>
                                        <input
                                            type="text"
                                            id="songName"
                                            value={songFormData.songName}
                                            onChange={(e) => setSongFormData({ ...songFormData, songName: e.target.value })}
                                            placeholder="Desi Girl"
                                            required
                                        />
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="artist">Artist</label>
                                        <input
                                            type="text"
                                            id="artist"
                                            value={songFormData.artist}
                                            onChange={(e) => setSongFormData({ ...songFormData, artist: e.target.value })}
                                            placeholder="Sunidhi Chauhan"
                                        />
                                    </div>
                                    <div className="form-group-inline">
                                        <label htmlFor="songFile">Audio File *</label>
                                        <input type="file" id="songFile" accept="audio/*" onChange={handleSongFileChange} required />
                                    </div>
                                </div>
                                <div className="form-actions">
                                    <Button label={loading ? 'Uploading...' : 'Add Song'} onClick={() => { }} disabled={loading || !songFile} />
                                </div>
                            </form>
                        </div>

                        <div className="section-card">
                            <h2 className="section-title">📂 Uploaded Ceremony Songs</h2>
                            {ceremonySongs.length === 0 ? (
                                <p>No ceremony songs uploaded yet.</p>
                            ) : (
                                <table className="admins-table">
                                    <thead>
                                        <tr>
                                            <th>Ceremony</th>
                                            <th>Song</th>
                                            <th>Artist</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ceremonySongs.map((song) => (
                                            <tr key={song._id}>
                                                <td><strong>{song.ceremonyName}</strong></td>
                                                <td>{song.songName}</td>
                                                <td>{song.artist || '-'}</td>
                                                <td>
                                                    <button className="delete-btn" onClick={() => handleDeleteSong(song._id)}>Delete</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SuperAdminDashboard;

